import { Fragment, useEffect, useState } from "react";
import { Shell, Card } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { apiError, http, DAYS, GROUPS, groupLabel, monthName, SEDES } from "../lib/api";
import { FeesTable, PayState } from "../components/Billing";
import { FinanceSummary } from "../components/FinanceSummary";
import { ChangePassword } from "../components/ChangePassword";
import { EnrollmentManager } from "../components/EnrollmentManager";
import { LogopediaCalendar, SessionNotes } from "../components/LogopediaCalendar";
import { CentroWeekBoard } from "../components/CentroWeekBoard";

const TABS = [
  ["franjas", "Franjas y horario"],
  ["alumnos", "Alumnos y mensualidades"],
  ["matriculas", "Matrículas y horarios"],
  ["recuperaciones", "Ausencias y recuperaciones"],
  ["examenes", "Exámenes"],
  ["cambios", "Cambios de grupo"],
  ["logopedia", "Logopedia: pacientes y bonos"],
  ["lcalendario", "Calendario de Logopedia"],
  ["fichas", "Fichas de trabajo"],
  ["directorio", "Listado general"],
  ["resumen", "Resumen económico"],
  ["ajustes", "Precios y cierres"],
  ["cuenta", "Mi contraseña"],
  ["avisos", "Avisos WhatsApp"],
];

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full min-w-[640px] border-collapse text-left text-base">
      <thead>
        <tr className="border-b border-border">
          {head.map((h) => <th key={h} className="py-3 pr-4 font-medium text-ink-700">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={head.length} className="py-4 text-ink-700">Sin datos todavía.</td></tr>
        )}
        {rows}
      </tbody>
    </table>
  </div>
);

export default function Admin() {
  const [tab, setTab] = useState("franjas");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [roster, setRoster] = useState([]);
  const [students, setStudents] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [exams, setExams] = useState([]);
  const [changes, setChanges] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [prices, setPrices] = useState({});
  const [closures, setClosures] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [sede, setSede] = useState("xalo");
  const [lsessions, setLsessions] = useState([]);
  const [lpatients, setLpatients] = useState([]);
  const [lchanges, setLchanges] = useState([]);
  const [slotForm, setSlotForm] = useState({ day: 1, start: "", end: "", stage_group: "",
    kind: "grande", block: "", min_students: 0, label: "", teacher: "" });
  const [closureForm, setClosureForm] = useState({ date: "", reason: "" });
  const [search, setSearch] = useState("");
  const [openFees, setOpenFees] = useState(null);
  const [openBonos, setOpenBonos] = useState(null);
  const [openSessions, setOpenSessions] = useState(null);
  const [psessions, setPsessions] = useState([]);
  const [manualForm, setManualForm] = useState({ date: "", start: "15:30", room: 1, status: "consumida" });
  const [sheets, setSheets] = useState([]);
  const [sheetForm, setSheetForm] = useState({ patient_id: "", title: "", due_date: "" });
  const [sortBy, setSortBy] = useState("name");
  const [waCfg, setWaCfg] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [teamPhones, setTeamPhones] = useState("");
  const [confirmBaja, setConfirmBaja] = useState(null);
  const [onlyDebt, setOnlyDebt] = useState(false);

  const load = async () => {
    const [r, s, a, e, c, d, p, cl, n, w] = await Promise.all([
      http.get("/admin/slots-roster"), http.get("/admin/students"), http.get("/admin/absences"),      http.get("/admin/exams"), http.get("/admin/group-changes"), http.get("/admin/directory"),
      http.get("/settings/pricing"), http.get("/closures"), http.get("/admin/notifications"),
      http.get("/admin/whatsapp/config"),
    ]);
    setRoster(r.data); setStudents(s.data); setAbsences(a.data); setExams(e.data);
    setChanges(c.data); setDirectory(d.data); setPrices(p.data.prices); setClosures(cl.data);
    setNotifs(n.data); setWaCfg(w.data);
    setTeamPhones((w.data.team_phones || []).join(", "));
  };

  const loadLogo = async () => {
    const [ls, lp, lc, ws] = await Promise.all([
      http.get("/admin/logopedia/sessions", { params: { sede } }),
      http.get("/admin/logopedia/patients", { params: { sede } }),
      http.get("/admin/logopedia/changes", { params: { sede } }),
      http.get("/admin/logopedia/worksheets"),
    ]);
    setLsessions(ls.data); setLpatients(lp.data); setLchanges(lc.data); setSheets(ws.data);
  };

  useEffect(() => { load().catch((err) => setError(apiError(err))); }, []);
  useEffect(() => { loadLogo().catch(() => {}); }, [sede]);

  const act = async (fn, okMsg, logo = false) => {
    setMsg(""); setError("");
    try {
      await fn(); setMsg(okMsg); await load();
      if (logo) await loadLogo();
      if (openSessions) await loadSessions(openSessions);
    } catch (err) { setError(apiError(err)); }
  };

  const loadSessions = async (pid) => {
    const { data } = await http.get(`/admin/logopedia/patients/${pid}/sessions`);
    setPsessions(data);
  };

  const toggleSessions = async (pid) => {
    setMsg(""); setError("");
    if (openSessions === pid) { setOpenSessions(null); setPsessions([]); return; }
    try { await loadSessions(pid); setOpenSessions(pid); }
    catch (err) { setError(apiError(err)); }
  };

  const month = new Date().toISOString().slice(0, 7);
  const visibleStudents = students
    .filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    .filter((s) => !onlyDebt || s.past_due_total > 0 || s.current_pending > 0)
    .sort((a, b) => (b.past_due_total || 0) - (a.past_due_total || 0));
  const filteredDir = directory
    .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (sortBy === "name" ? a.name.localeCompare(b.name) : a.group.localeCompare(b.group)));

  return (
    <Shell title="Panel del equipo SISU" subtitle="Gestión del Centro de Estudios y de Logopedia." testid="admin-page">
      <div className="mb-6 flex flex-wrap gap-3">
        {TABS.map(([key, label]) => (
          <Button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            variant={tab === key ? "default" : "outline"} className="h-12 text-base">
            {label}
          </Button>
        ))}
      </div>
      {msg && <p data-testid="admin-success" className="mb-5 rounded-xl bg-logo-light p-4 text-lg text-logo-text">{msg}</p>}
      {error && <p data-testid="admin-error" className="mb-5 rounded-xl bg-red-50 p-4 text-lg text-red-700">{error}</p>}

      {tab === "franjas" && (
        <div className="space-y-8">
          <Card>
            <h2 className="font-head text-2xl font-medium">Crear una franja nueva</h2>
            <p className="mt-1 text-base text-ink-700">
              Cada franja de grupo grande admite 8 alumnos y cada franja de Grupo Reducido, 3. Las
              familias solo ven si la franja está disponible o completa.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <Label className="text-base">Día</Label>
                <select data-testid="slot-day-select" value={slotForm.day}
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  onChange={(e) => setSlotForm({ ...slotForm, day: Number(e.target.value) })}>
                  {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>{DAYS[d]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-base">Hora inicio</Label>
                <Input data-testid="slot-start-input" type="time" className="mt-2 h-14 text-lg"
                  value={slotForm.start} onChange={(e) => setSlotForm({ ...slotForm, start: e.target.value })} />
              </div>
              <div>
                <Label className="text-base">Hora fin</Label>
                <Input data-testid="slot-end-input" type="time" className="mt-2 h-14 text-lg"
                  value={slotForm.end} onChange={(e) => setSlotForm({ ...slotForm, end: e.target.value })} />
              </div>
              <div>
                <Label className="text-base">Grupo</Label>
                <select data-testid="slot-group-select" value={slotForm.stage_group}
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  onChange={(e) => setSlotForm({ ...slotForm, stage_group: e.target.value })}>
                  <option value="">Elegir...</option>
                  {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                  <option value="eso2n_bach">ESO 3º-4º y Bachillerato (combinado)</option>
                  <option value="gr">Grupo Reducido (GR)</option>
                </select>
              </div>
              <div>
                <Label className="text-base">Tipo</Label>
                <select data-testid="slot-kind-select" value={slotForm.kind}
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  onChange={(e) => setSlotForm({ ...slotForm, kind: e.target.value })}>
                  <option value="grande">Grupo grande</option>
                  <option value="gr">Grupo Reducido</option>
                  <option value="ingles">Inglés</option>
                </select>
              </div>
              <div>
                <Label className="text-base">Bloque (solo ESO/Bach.)</Label>
                <select data-testid="slot-block-select" value={slotForm.block}
                  className="mt-2 h-14 w-full rounded-lg border border-border bg-white px-3 text-lg"
                  onChange={(e) => setSlotForm({ ...slotForm, block: e.target.value })}>
                  <option value="">Sin bloque</option>
                  <option value="LETRAS">LETRAS</option>
                  <option value="CIENCIAS">CIENCIAS</option>
                </select>
              </div>
              <div>
                <Label className="text-base">Mínimo de alumnos</Label>
                <Input data-testid="slot-min-input" type="number" className="mt-2 h-14 text-lg"
                  value={slotForm.min_students}
                  onChange={(e) => setSlotForm({ ...slotForm, min_students: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-base">Nombre visible de la franja</Label>
                <Input data-testid="slot-label-input" className="mt-2 h-14 text-lg"
                  value={slotForm.label} onChange={(e) => setSlotForm({ ...slotForm, label: e.target.value })} />
              </div>
              <div>
                <Label className="text-base">Profesional que imparte la franja</Label>
                <Input data-testid="slot-teacher-input" className="mt-2 h-14 text-lg"
                  value={slotForm.teacher} onChange={(e) => setSlotForm({ ...slotForm, teacher: e.target.value })} />
              </div>
            </div>
            <Button data-testid="slot-create-btn" className="mt-4 h-14 text-lg"
              disabled={!slotForm.start || !slotForm.end || !slotForm.stage_group}
              onClick={() => act(() => http.post("/admin/slots", {
                ...slotForm, block: slotForm.block || null, label: slotForm.label || null,
                teacher: slotForm.teacher || null,
              }), "Franja creada.")}>
              Crear franja
            </Button>
          </Card>
          <CentroWeekBoard roster={roster} onToggleClosed={(s) => act(
            () => http.put(`/admin/slots/${s.id}/closed`, { closed: !s.closed }),
            s.closed ? "Grupo reabierto." : "Grupo marcado como completo.")} />
          <Card>
            <h2 className="mb-4 font-head text-2xl font-medium">Franjas y alumnos matriculados</h2>
            <p className="mb-4 text-base text-ink-700">
              Desactivar una franja la retira de la matriculación y de los horarios nuevos, pero
              conserva sus alumnos y todo su historial. Solo se pueden borrar franjas sin alumnos.
            </p>
            <Table testid="roster-table" head={["Día", "Hora", "Grupo", "Bloque/Tipo", "Profesional", "Ocupación", "Estado", "Alumnos", ""]}
              rows={roster.map((s) => (
                <tr key={s.id} className={`border-b border-border ${s.active === false ? "bg-muted/50" : ""}`}
                  data-testid={`roster-row-${s.id}`}>
                  <td className="py-3 pr-4">{DAYS[s.day]}</td>
                  <td className="py-3 pr-4">{s.start}-{s.end}</td>
                  <td className="py-3 pr-4">{s.label || groupLabel(s.stage_group)}</td>
                  <td className="py-3 pr-4">{s.block || (s.kind === "gr" ? "GR" : s.kind === "ingles" ? "Inglés" : "—")}</td>
                  <td className="py-3 pr-4">{s.teacher || "—"}</td>
                  <td className="py-3 pr-4">{s.taken}/{s.capacity}{s.full ? " (completo)" : s.pending_min ? ` (mín. ${s.min_students})` : ""}</td>
                  <td className="py-3 pr-4" data-testid={`slot-state-${s.id}`}>
                    {s.active === false
                      ? <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">Desactivada</span>
                      : <span className="rounded-full bg-logo-light px-3 py-1 text-sm font-medium text-logo-text">Activa</span>}
                  </td>
                  <td className="py-3 pr-4">{s.students.join(", ") || "—"}</td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`slot-active-${s.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => act(() => http.put(`/admin/slots/${s.id}/active`,
                          { active: s.active === false }),
                          s.active === false ? "Franja reactivada." : "Franja desactivada: deja de ofrecerse y no se ha borrado nada.")}>
                        {s.active === false ? "Reactivar" : "Desactivar"}
                      </Button>
                      <Button data-testid={`slot-delete-${s.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => act(() => http.delete(`/admin/slots/${s.id}`), "Franja eliminada.")}>
                        Borrar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))} />
          </Card>
        </div>
      )}

      {tab === "matriculas" && (
        <EnrollmentManager students={students} onChanged={load} />
      )}

      {tab === "alumnos" && (
        <Card>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-head text-2xl font-medium">Alumnos, horas y mensualidad ({monthName(month)})</h2>
            <div className="flex flex-wrap items-center gap-3">
              <Input data-testid="students-search" value={search} placeholder="Buscar alumno"
                className="h-12 w-56 text-base" onChange={(e) => setSearch(e.target.value)} />
              <Button data-testid="students-debt-filter" variant={onlyDebt ? "default" : "outline"}
                className="h-12 text-base" onClick={() => setOnlyDebt(!onlyDebt)}>
                {onlyDebt ? "Ver todos los alumnos" : "Ver solo quien debe"}
              </Button>
            </div>
          </div>
          <Table testid="students-table" head={["Alumno", "Agrupación", "Familia", "Contacto", "Horas GG", "Horas GR", "Total del mes", "Mes actual", "Deudas anteriores", "Acciones"]}
            rows={visibleStudents.map((s) => (
              <tr key={s.id} className={`border-b border-border ${s.past_due_total > 0 ? "bg-red-50" : ""}`}
                data-testid={`student-row-${s.id}`}>
                <td className="py-3 pr-4">{s.name}</td>
                <td className="py-3 pr-4">{groupLabel(s.stage_group)}</td>
                <td className="py-3 pr-4">{s.family?.name} <br /><span className="text-ink-700">{s.family?.email}</span></td>
                <td className="py-3 pr-4">{s.family?.phone}</td>
                <td className="py-3 pr-4">{s.weekly_hours}</td>
                <td className="py-3 pr-4">{s.gr_weekly_hours}</td>
                <td className="py-3 pr-4">
                  <strong>{s.monthly_fee} €</strong>
                  <br /><span className="text-sm text-ink-700">
                    {s.big_fee} € grupo + {s.gr_sessions_month} ses. GR = {s.gr_fee} €
                  </span>
                </td>
                <td className="py-3 pr-4" data-testid={`current-month-${s.id}`}>
                  {s.pause_month === month ? (
                    <span className="rounded-full bg-cream px-3 py-1 text-sm font-medium">En pausa</span>
                  ) : s.current_pending > 0 ? (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-900">
                      Pendiente {s.current_pending.toFixed(2)} €
                    </span>
                  ) : (
                    <span className="rounded-full bg-logo-light px-3 py-1 text-sm font-medium text-logo-text">
                      Pagado ✓
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4" data-testid={`past-due-${s.id}`}>
                  {s.past_due_total > 0 ? (
                    <div className="rounded-lg bg-red-100 p-2 text-sm text-red-800">
                      <p className="font-semibold">⚠ Debe {s.past_due_total.toFixed(2)} €</p>
                      <p className="mt-1">
                        {s.past_due.map((d) => `${monthName(d.month)}: ${d.pending.toFixed(2)} €`).join(" · ")}
                      </p>
                    </div>
                  ) : (
                    <span className="text-sm text-ink-700">Sin deudas anteriores ✓</span>
                  )}
                </td>
                <td className="py-3" data-testid={`student-actions-${s.id}`}>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`fees-toggle-${s.id}`} variant="outline" className="h-10 text-sm"
                      onClick={() => setOpenFees(openFees === s.id ? null : s.id)}>
                      {openFees === s.id ? "Ocultar cuotas" : "Cuotas del curso"}
                    </Button>
                    {confirmBaja === s.id ? (
                      <>
                        <Button data-testid={`confirm-baja-student-${s.id}`} className="h-10 bg-red-700 text-sm hover:bg-red-800"
                          onClick={() => act(async () => {
                            await http.delete(`/admin/students/${s.id}`);
                            setConfirmBaja(null);
                          }, `${s.name} ha sido dado/a de baja: sus datos se han eliminado y sus plazas están libres.`)}>
                          Confirmar baja
                        </Button>
                        <Button variant="outline" className="h-10 text-sm" onClick={() => setConfirmBaja(null)}>
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <Button data-testid={`baja-student-${s.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => setConfirmBaja(s.id)}>
                        Dar de baja
                      </Button>
                    )}
                  </div>
                  {openFees === s.id && (
                    <div className="mt-4 min-w-[560px]" data-testid={`fees-panel-${s.id}`}>
                      <FeesTable rows={s.fees} totalDebt={s.total_debt} testid={`admin-fees-${s.id}`}
                        onSetPaid={(month, paid) => act(
                          () => http.put(`/admin/students/${s.id}/payments`, { month, paid }),
                          "Cantidad abonada registrada.")} />
                    </div>
                  )}
                </td>
              </tr>
            ))} />
        </Card>
      )}

      {tab === "recuperaciones" && (
        <Card>
          <h2 className="mb-4 font-head text-2xl font-medium">Ausencias y recuperaciones</h2>
          <Table testid="absences-table" head={["Alumno", "Fecha de la ausencia", "Franja", "Límite para recuperar", "Estado"]}
            rows={absences.map((a) => (
              <tr key={a.id} className="border-b border-border">
                <td className="py-3 pr-4">{a.student_name}</td>
                <td className="py-3 pr-4">{a.date}</td>
                <td className="py-3 pr-4">{a.slot}</td>
                <td className="py-3 pr-4">{a.deadline}</td>
                <td className="py-3 pr-4">{a.recovered ? `Recuperada (${a.recovery?.label})` : "Pendiente"}</td>
              </tr>
            ))} />
        </Card>
      )}

      {tab === "examenes" && (
        <Card>
          <h2 className="mb-4 font-head text-2xl font-medium">Exámenes apuntados por las familias</h2>
          <Table testid="exams-table" head={["Alumno", "Asignatura", "Fecha", "Notas"]}
            rows={exams.map((e) => (
              <tr key={e.id} className="border-b border-border">
                <td className="py-3 pr-4">{e.student_name}</td>
                <td className="py-3 pr-4">{e.subject}</td>
                <td className="py-3 pr-4">{e.date}</td>
                <td className="py-3 pr-4">{e.note || "—"}</td>
              </tr>
            ))} />
        </Card>
      )}

      {tab === "cambios" && (
        <Card>
          <h2 className="mb-4 font-head text-2xl font-medium">Historial de cambios de grupo</h2>
          <Table testid="changes-table" head={["Alumno", "Desde", "Hasta", "Cuándo"]}
            rows={changes.map((c) => (
              <tr key={c.id} className="border-b border-border">
                <td className="py-3 pr-4">{c.student_name}</td>
                <td className="py-3 pr-4">{c.from}</td>
                <td className="py-3 pr-4">{c.to}</td>
                <td className="py-3 pr-4">{c.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))} />
        </Card>
      )}

      {tab === "lcalendario" && (
        <LogopediaCalendar patients={lpatients} onChanged={loadLogo} />
      )}

      {tab === "logopedia" && (
        <div className="space-y-8">
          <div className="flex gap-3">
            {Object.entries(SEDES).map(([k, l]) => (
              <Button key={k} data-testid={`admin-sede-${k}-btn`} variant={sede === k ? "default" : "outline"}
                className="h-12 text-base" onClick={() => setSede(k)}>Sede de {l}</Button>
            ))}
          </div>
          <Card>
            <h2 className="mb-4 font-head text-2xl font-medium">Pacientes y bonos — {SEDES[sede]}</h2>
            <Table testid="lpatients-table" head={["Paciente", "Familia", "Bono", "Horario fijo", "Realizadas", "Quedan", "Anamnesis", "Acciones"]}
              rows={lpatients.map((p) => (
                <Fragment key={p.id}>
                <tr className="border-b border-border" data-testid={`lpatient-row-${p.id}`}>
                  <td className="py-3 pr-4">{p.name}</td>
                  <td className="py-3 pr-4">{p.family?.name}<br /><span className="text-ink-700">{p.family?.phone}</span></td>
                  <td className="py-3 pr-4">{p.bono_total}
                    <br /><span className="text-sm text-ink-700">
                      Bono {p.bono_number} · deuda {p.total_debt?.toFixed(2)} €
                      {p.pause_month ? ` · pausa ${p.pause_month}` : ""}
                    </span>
                  </td>
                  <td className="py-3 pr-4" data-testid={`lpatient-mode-${p.id}`}>
                    {p.fixed_schedule?.length
                      ? p.fixed_schedule.map((f) => `${f.day_name} ${f.start}`).join(" · ")
                      : "sin horario fijo"}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(p.fixed_schedule || []).map((f, i) => (
                        <select key={i} data-testid={`sched-time-${p.id}-${f.day}`} defaultValue={f.start}
                          className="h-10 rounded-lg border border-border bg-white px-2 text-sm"
                          onChange={(e) => {
                            const next = (p.fixed_schedule || []).map((x) => ({
                              day: x.day, start: x.day === f.day ? e.target.value : x.start,
                            }));
                            act(() => http.put(`/admin/logopedia/patients/${p.id}/schedule`,
                              { fixed_schedule: next }),
                              "Horario fijo actualizado y sesiones futuras rehechas.", true);
                          }}>
                          {["15:30", "16:15", "17:00", "17:45", "18:30", "19:15"].map((t) => (
                            <option key={t} value={t}>{f.day_name} · {t}</option>
                          ))}
                        </select>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 pr-4">{p.bono_used}</td>
                  <td className="py-3 pr-4" data-testid={`lpatient-left-${p.id}`}>{p.bono_left}</td>
                  <td className="py-3 pr-4 max-w-[280px]">
                    {p.anamnesis
                      ? Object.entries(p.anamnesis).filter(([, v]) => v).map(([k, v]) => (
                          <p key={k} className="text-sm text-ink-700"><strong>{k}:</strong> {v}</p>))
                      : "Sin rellenar"}
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      {[4, 8].map((n) => (
                        <Button key={n} data-testid={`renew-${p.id}-${n}`} variant="outline" className="h-10 text-sm"
                          onClick={() => act(() => http.post("/admin/logopedia/bonos", { patient_id: p.id, sessions: n }),
                            p.fixed_schedule?.length
                              ? "Bono renovado: se han generado las sesiones con el mismo día y hora."
                              : "Bono renovado. Asigne el horario fijo del paciente.", true)}>
                          +{n}
                        </Button>
                      ))}
                      <Button data-testid={`bonos-toggle-${p.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => setOpenBonos(openBonos === p.id ? null : p.id)}>
                        {openBonos === p.id ? "Ocultar bonos" : "Historial de bonos"}
                      </Button>
                      <Button data-testid={`sessions-toggle-${p.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => toggleSessions(p.id)}>
                        {openSessions === p.id ? "Ocultar sesiones" : "Editar sesiones del bono"}
                      </Button>
                      {confirmBaja === p.id ? (
                        <>
                          <Button data-testid={`confirm-baja-patient-${p.id}`} className="h-10 bg-red-700 text-sm hover:bg-red-800"
                            onClick={() => act(async () => {
                              await http.delete(`/admin/logopedia/patients/${p.id}`);
                              setConfirmBaja(null);
                            }, `${p.name} ha sido dado/a de baja: su expediente se ha eliminado y sus sesiones están libres.`, true)}>
                            Confirmar baja
                          </Button>
                          <Button variant="outline" className="h-10 text-sm" onClick={() => setConfirmBaja(null)}>
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <Button data-testid={`baja-patient-${p.id}`} variant="outline" className="h-10 text-sm"
                          onClick={() => setConfirmBaja(p.id)}>
                          Dar de baja
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {(openBonos === p.id || openSessions === p.id) && (
                  <tr className="border-b border-border">
                    <td colSpan={8} className="py-3">
                    {openBonos === p.id && (
                      <div className="mt-2 overflow-x-auto" data-testid={`bonos-panel-${p.id}`}>
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="border-b border-border text-ink-700">
                              <th className="py-2 pr-3">Bono</th>
                              <th className="py-2 pr-3">Sesiones</th>
                              <th className="py-2 pr-3">Importe</th>
                              <th className="py-2 pr-3">Inicio</th>
                              <th className="py-2 pr-3">Fin</th>
                              <th className="py-2 pr-3">Pendiente</th>
                              <th className="py-2 pr-3">Estado</th>
                              <th className="py-2">Abonado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.bonos.map((b) => (
                              <tr key={b.id} className="border-b border-border" data-testid={`admin-bono-${b.id}`}>
                                <td className="py-2 pr-3">Bono {b.number}{b.active ? " (en curso)" : ""}</td>
                                <td className="py-2 pr-3">{b.sessions}</td>
                                <td className="py-2 pr-3">{b.price.toFixed(2)} €</td>
                                <td className="py-2 pr-3">{b.start_date}</td>
                                <td className="py-2 pr-3">{b.end_date || "—"}</td>
                                <td className="py-2 pr-3">{b.pending.toFixed(2)} €</td>
                                <td className="py-2 pr-3"><PayState status={b.status} testid={`admin-bono-state-${b.id}`} /></td>
                                <td className="py-2">
                                  <div className="flex gap-2">
                                    <Input id={`bp-${b.id}`} data-testid={`bono-paid-input-${b.id}`} type="number"
                                      step="0.01" min="0" defaultValue={b.paid} className="h-10 w-24 text-sm" />
                                    <Button data-testid={`bono-paid-save-${b.id}`} variant="outline" className="h-10 text-sm"
                                      onClick={() => act(() => http.put(`/admin/logopedia/bonos/${b.id}/payment`, {
                                        month: b.start_date.slice(0, 7),
                                        paid: Number(document.getElementById(`bp-${b.id}`).value),
                                      }), "Cantidad abonada registrada.", true)}>
                                      Guardar
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {openSessions === p.id && (
                      <div className="mt-2 overflow-x-auto rounded-xl bg-cream p-4"
                        data-testid={`sessions-panel-${p.id}`}>
                        <h3 className="font-head text-lg font-medium">Sesiones de {p.name}</h3>
                        <p className="mt-1 text-sm text-ink-700">
                          Control total del equipo: marque cada sesión como realizada, ausencia o
                          pendiente de recuperar. Ninguna sesión se descuenta sola por la fecha.
                          También puede añadir sesiones antiguas hechas fuera de la web.
                        </p>
                        {p.bonos.filter((b) => b.active).map((b) => (
                          <div key={b.id} className="mt-4 flex flex-wrap items-end gap-3">
                            <div>
                              <Label className="text-sm">Sesiones del bono {b.number}</Label>
                              <div className="mt-2 flex gap-2">
                                {[4, 8].map((n) => (
                                  <Button key={n} data-testid={`bono-sessions-${b.id}-${n}`}
                                    variant={b.sessions === n ? "default" : "outline"} className="h-10 text-sm"
                                    onClick={() => act(() => http.put(`/admin/logopedia/bonos/${b.id}/sessions`,
                                      { sessions: n }),
                                      `Bono cambiado a ${n} sesiones. Se conservan pagos, sesiones hechas e historial.`, true)}>
                                    {n} sesiones
                                  </Button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm">Sesiones realizadas del bono {b.number} (0–{b.sessions})</Label>
                              <Input key={`${b.id}-${p.bono_used}`} id={`used-${b.id}`}
                                data-testid={`used-input-${b.id}`} type="number"
                                min="0" max={b.sessions} defaultValue={p.bono_used}
                                className="mt-2 h-10 w-24 text-sm" />
                            </div>
                            <Button data-testid={`used-save-${b.id}`} variant="outline" className="h-10 text-sm"
                              onClick={() => act(() => http.put(`/admin/logopedia/bonos/${b.id}/sessions-used`, {
                                used: Number(document.getElementById(`used-${b.id}`).value),
                              }), "Contador de sesiones corregido.", true)}>
                              Guardar contador
                            </Button>
                            <Button data-testid={`used-reset-${b.id}`} variant="outline" className="h-10 text-sm"
                              onClick={() => act(() => http.put(`/admin/logopedia/bonos/${b.id}/sessions-used`, { used: null }),
                                "Contador recalculado automáticamente.", true)}>
                              Volver al cálculo automático
                            </Button>
                          </div>
                        ))}
                        <div className="mt-5 flex flex-wrap items-end gap-3">
                          <div>
                            <Label className="text-sm">Fecha</Label>
                            <Input data-testid={`manual-date-${p.id}`} type="date" className="mt-2 h-10 text-sm"
                              value={manualForm.date}
                              onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-sm">Hora</Label>
                            <select data-testid={`manual-start-${p.id}`} value={manualForm.start}
                              className="mt-2 h-10 rounded-lg border border-border bg-white px-2 text-sm"
                              onChange={(e) => setManualForm({ ...manualForm, start: e.target.value })}>
                              {["15:30", "16:15", "17:00", "17:45", "18:30", "19:15"].map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-sm">Consulta</Label>
                            <select data-testid={`manual-room-${p.id}`} value={manualForm.room}
                              className="mt-2 h-10 rounded-lg border border-border bg-white px-2 text-sm"
                              onChange={(e) => setManualForm({ ...manualForm, room: Number(e.target.value) })}>
                              {[1, 2].map((r) => <option key={r} value={r}>Consulta {r}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-sm">Estado</Label>
                            <select data-testid={`manual-status-${p.id}`} value={manualForm.status}
                              className="mt-2 h-10 rounded-lg border border-border bg-white px-2 text-sm"
                              onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}>
                              <option value="consumida">Realizada (cuenta en el bono)</option>
                              <option value="ausencia">Ausencia</option>
                              <option value="recuperar">Pendiente de recuperar</option>
                              <option value="historico">Histórica (bono anterior)</option>
                              <option value="reservada">Reservada</option>
                              <option value="cancelada">Cancelada</option>
                            </select>
                          </div>
                          <Button data-testid={`manual-add-${p.id}`} className="h-10 text-sm"
                            disabled={!manualForm.date}
                            onClick={() => act(async () => {
                              await http.post("/admin/logopedia/sessions/manual", {
                                patient_id: p.id, ...manualForm,
                              });
                              setManualForm({ date: "", start: "15:30", room: 1, status: "consumida" });
                            }, "Sesión añadida manualmente.", true)}>
                            Añadir sesión
                          </Button>
                        </div>
                        <table className="mt-5 w-full text-left text-sm">
                          <thead>
                            <tr className="border-b border-border text-ink-700">
                              <th className="py-2 pr-3">Fecha</th>
                              <th className="py-2 pr-3">Hora</th>
                              <th className="py-2 pr-3">Consulta</th>
                              <th className="py-2 pr-3">Estado</th>
                              <th className="py-2">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {psessions.length === 0 && (
                              <tr><td colSpan={5} className="py-3 text-ink-700">Sin sesiones registradas.</td></tr>
                            )}
                            {psessions.map((s) => (
                              <tr key={s.id} className="border-b border-border" data-testid={`edit-session-${s.id}`}>
                                <td className="py-2 pr-3">{s.date}</td>
                                <td className="py-2 pr-3">{s.start}</td>
                                <td className="py-2 pr-3">Consulta {s.room}</td>
                                <td className="py-2 pr-3">
                                  <div className="flex flex-wrap gap-1">
                                    {[["consumida", "✅ Realizada"], ["ausencia", "❌ Ausencia"],
                                      ["recuperar", "🔄 Recuperar"], ["reservada", "Reservada"],
                                      ["cancelada", "Cancelada"], ["historico", "Histórica"]].map(([v, l]) => (
                                      <Button key={v} data-testid={`session-mark-${s.id}-${v}`}
                                        variant={s.status === v ? "default" : "outline"}
                                        className="h-9 px-2 text-xs"
                                        onClick={() => act(() => http.put(
                                          `/admin/logopedia/sessions/${s.id}/status`, { status: v }),
                                          "Sesión marcada. El bono y el panel de la familia se han actualizado.", true)}>
                                        {l}
                                      </Button>
                                    ))}
                                  </div>
                                </td>
                                <td className="py-2">
                                  {s.manual ? (
                                    <Button data-testid={`session-delete-${s.id}`} variant="outline" className="h-10 text-sm"
                                      onClick={() => act(() => http.delete(
                                        `/admin/logopedia/sessions/${s.id}/manual`),
                                        "Sesión manual eliminada.", true)}>
                                      Eliminar
                                    </Button>
                                  ) : <span className="text-ink-700">De la web</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </td>
                  </tr>
                )}
                </Fragment>
              ))} />
          </Card>
          <SessionNotes sessions={lsessions}
            onSave={(url, body, okMsg) => act(() => http.put(url, body), okMsg, true)} />
          <Card>
            <h2 className="mb-4 font-head text-2xl font-medium">Cambios y cancelaciones — {SEDES[sede]}</h2>
            <Table testid="lchanges-table" head={["Paciente", "Sesión", "Acción", "Aviso (horas)", "¿Menos de 6h?"]}
              rows={lchanges.map((c) => (
                <tr key={c.id} className="border-b border-border">
                  <td className="py-3 pr-4">{c.patient_name}</td>
                  <td className="py-3 pr-4">{c.date} {c.start}</td>
                  <td className="py-3 pr-4">{c.action}</td>
                  <td className="py-3 pr-4">{c.hours_notice}</td>
                  <td className="py-3 pr-4">{c.late ? "Sí, se descuenta del bono" : "No"}</td>
                </tr>
              ))} />
          </Card>
        </div>
      )}

      {tab === "fichas" && (
        <div className="space-y-8">
          <Card>
            <h2 className="mb-4 font-head text-2xl font-medium">Nueva ficha de trabajo</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-base">Paciente</Label>
                <select data-testid="sheet-patient-select" value={sheetForm.patient_id}
                  className="mt-2 h-12 w-full rounded-lg border border-border bg-white px-3 text-base"
                  onChange={(e) => setSheetForm({ ...sheetForm, patient_id: e.target.value })}>
                  <option value="">Seleccionar paciente...</option>
                  {lpatients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-base">Título de la ficha</Label>
                <Input data-testid="sheet-title-input" className="mt-2 h-12 text-base"
                  value={sheetForm.title}
                  onChange={(e) => setSheetForm({ ...sheetForm, title: e.target.value })} />
              </div>
              <div>
                <Label className="text-base">Fecha de entrega</Label>
                <Input data-testid="sheet-date-input" type="date" className="mt-2 h-12 text-base"
                  value={sheetForm.due_date}
                  onChange={(e) => setSheetForm({ ...sheetForm, due_date: e.target.value })} />
              </div>
            </div>
            <Button data-testid="sheet-create-btn" className="mt-4 h-12 text-base"
              disabled={!sheetForm.patient_id || !sheetForm.title || !sheetForm.due_date}
              onClick={() => act(async () => {
                await http.post("/admin/logopedia/worksheets", sheetForm);
                setSheetForm({ patient_id: "", title: "", due_date: "" });
              }, "Ficha de trabajo asignada.", true)}>
              Asignar la ficha
            </Button>
          </Card>
          <Card>
            <h2 className="mb-4 font-head text-2xl font-medium">Historial de fichas</h2>
            <Table testid="sheets-table" head={["Paciente", "Ficha", "Entrega", "Estado", "Observaciones de la familia"]}
              rows={sheets.map((w) => (
                <tr key={w.id} className="border-b border-border" data-testid={`sheet-row-${w.id}`}>
                  <td className="py-3 pr-4">{w.patient_name}</td>
                  <td className="py-3 pr-4">{w.title}</td>
                  <td className="py-3 pr-4">{w.due_date}</td>
                  <td className="py-3 pr-4" data-testid={`sheet-status-${w.id}`}>
                    {w.status === "completada" ? "COMPLETADA" : "NO COMPLETADA"}
                  </td>
                  <td className="py-3">{w.notes || "—"}</td>
                </tr>
              ))} />
          </Card>
        </div>
      )}

      {tab === "directorio" && (
        <Card>
          <h2 className="font-head text-2xl font-medium">Listado general de alumnado y pacientes</h2>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-base">Buscar por nombre</Label>
              <Input data-testid="directory-search-input" className="mt-2 h-14 text-lg" value={search}
                onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Button data-testid="sort-name-btn" variant={sortBy === "name" ? "default" : "outline"}
              className="h-14 text-base" onClick={() => setSortBy("name")}>Ordenar por nombre</Button>
            <Button data-testid="sort-group-btn" variant={sortBy === "group" ? "default" : "outline"}
              className="h-14 text-base" onClick={() => setSortBy("group")}>Ordenar por curso/etapa</Button>
          </div>
          <div className="mt-6">
            <Table testid="directory-table" head={["Nombre", "Módulo", "Etapa/Grupo", "Sede", "Contacto"]}
              rows={filteredDir.map((d, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-3 pr-4">{d.name}</td>
                  <td className="py-3 pr-4">{d.module}</td>
                  <td className="py-3 pr-4">{d.group === "-" ? "—" : groupLabel(d.group)}</td>
                  <td className="py-3 pr-4">{d.sede === "-" ? "—" : SEDES[d.sede]}</td>
                  <td className="py-3 pr-4">{d.contact}</td>
                </tr>
              ))} />
          </div>
        </Card>
      )}

      {tab === "resumen" && <FinanceSummary />}

      {tab === "cuenta" && <ChangePassword testid="team-password" />}

      {tab === "ajustes" && (
        <div className="space-y-8">
          <Card>
            <h2 className="font-head text-2xl font-medium">Tarifas de la mensualidad</h2>
            <p className="mt-1 text-base text-ink-700">
              Importe por etapa y horas semanales en grupo grande, y precio por sesión de Grupo
              Reducido.
            </p>
            <div className="mt-4 space-y-4">
              {["Infantil", "Primaria", "ESO", "Bachillerato"].map((tier) => (
                <div key={tier}>
                  <p className="font-head text-lg font-medium">{tier}</p>
                  <div className="mt-2 grid gap-4 sm:grid-cols-4">
                    {["1", "2", "3", "4"].map((h) => (
                      <div key={h}>
                        <Label className="text-base">{h} h/semana</Label>
                        <Input data-testid={`price-${tier}-${h}-input`} type="number"
                          className="mt-2 h-14 text-lg"
                          value={prices?.[tier]?.[h] ?? ""}
                          onChange={(e) => setPrices({
                            ...prices,
                            [tier]: { ...(prices[tier] || {}), [h]: Number(e.target.value) },
                          })} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="max-w-xs">
                <Label className="text-base">Precio por sesión de Grupo Reducido</Label>
                <Input data-testid="price-gr-input" type="number" className="mt-2 h-14 text-lg"
                  value={prices?.gr_session ?? ""}
                  onChange={(e) => setPrices({ ...prices, gr_session: Number(e.target.value) })} />
              </div>
            </div>
            <Button data-testid="price-save-btn" className="mt-4 h-14 text-lg"
              onClick={() => act(() => http.put("/admin/settings/pricing", { prices }), "Tarifas guardadas.")}>
              Guardar tarifas
            </Button>
          </Card>
          <Card>
            <h2 className="font-head text-2xl font-medium">Días de cierre del centro</h2>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-base">Fecha</Label>
                <Input data-testid="closure-date-input" type="date" className="mt-2 h-14 text-lg"
                  value={closureForm.date} onChange={(e) => setClosureForm({ ...closureForm, date: e.target.value })} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-base">Motivo</Label>
                <Input data-testid="closure-reason-input" className="mt-2 h-14 text-lg"
                  value={closureForm.reason} onChange={(e) => setClosureForm({ ...closureForm, reason: e.target.value })} />
              </div>
              <Button data-testid="closure-add-btn" className="h-14 text-lg"
                disabled={!closureForm.date || !closureForm.reason}
                onClick={() => act(() => http.post("/admin/closures", closureForm), "Día de cierre añadido.")}>
                Añadir día de cierre
              </Button>
            </div>
            <div className="mt-6">
              <Table testid="closures-table" head={["Fecha", "Motivo", ""]}
                rows={closures.map((c) => (
                  <tr key={c.id} className="border-b border-border">
                    <td className="py-3 pr-4">{c.date}</td>
                    <td className="py-3 pr-4">{c.reason}</td>
                    <td className="py-3">
                      <Button data-testid={`closure-delete-${c.id}`} variant="outline" className="h-10 text-sm"
                        onClick={() => act(() => http.delete(`/admin/closures/${c.id}`), "Día de cierre eliminado.")}>
                        Borrar
                      </Button>
                    </td>
                  </tr>
                ))} />
            </div>
          </Card>
        </div>
      )}

      {tab === "avisos" && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-head text-2xl font-medium">Recordatorios por WhatsApp</h2>
            <Button data-testid="run-reminders-btn" className="h-14 text-lg"
              onClick={() => act(() => http.post("/reminders/run"), "Recordatorios de las próximas 24 horas procesados.")}>
              Enviar ahora los de las próximas 24 h
            </Button>
          </div>
          <p className="mt-2 text-base text-ink-700">
            El sistema envía automáticamente un recordatorio <strong>24 horas antes</strong> de cada
            clase y de cada sesión de logopedia, con el nombre del alumno, la fecha, la hora, el
            profesional y el centro.
          </p>

          <div className="mt-6 rounded-xl bg-cream p-5" data-testid="twilio-config-box">
            <h3 className="font-head text-xl font-medium">Estado de la conexión con Twilio</h3>
            {waCfg?.configured ? (
              <p className="mt-2 text-lg text-logo-text">
                Twilio está configurado: los recordatorios se envían de verdad.
              </p>
            ) : (
              <>
                <p className="mt-2 text-lg text-centro-text">
                  Twilio todavía no está configurado, así que los mensajes solo quedan registrados
                  aquí abajo como “simulado”.
                </p>
                <p className="mt-2 text-base text-ink-700">
                  Faltan estas claves: <strong>{(waCfg?.missing || []).join(", ") || "—"}</strong>.
                  Añádalas como variables secretas y el envío se activará automáticamente.
                </p>
              </>
            )}
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <div className="min-w-[220px]">
                <Label className="text-base">Móvil para la prueba (formato +34600111222)</Label>
                <Input data-testid="wa-test-phone-input" className="mt-2 h-14 text-lg"
                  placeholder="+34600111222" value={waPhone}
                  onChange={(e) => setWaPhone(e.target.value)} />
              </div>
              <Button data-testid="wa-test-btn" className="h-14 text-lg"
                disabled={!waCfg?.configured || !waPhone}
                onClick={() => act(() => http.post("/admin/whatsapp/test", { phone: waPhone }),
                  "Mensaje de prueba enviado.")}>
                Enviar mensaje de prueba
              </Button>
            </div>
            {!waCfg?.configured && (
              <p className="mt-3 text-base text-ink-700">
                El botón de prueba se activará automáticamente en cuanto estén las cuatro claves.
              </p>
            )}
          </div>

          <div className="mt-6 rounded-xl bg-cream p-5" data-testid="team-summary-box">
            <h3 className="font-head text-xl font-medium">Resumen matinal para el equipo</h3>
            <p className="mt-2 text-base text-ink-700">
              Cada mañana a las 8:00 h se envía al equipo un resumen por WhatsApp con las clases y
              las sesiones de logopedia del día, incluidas las ausencias comunicadas y el material a
              aportar. Indique los teléfonos del equipo separados por comas.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <div className="min-w-[280px] flex-1">
                <Label className="text-base">Teléfonos del equipo</Label>
                <Input data-testid="team-phones-input" className="mt-2 h-14 text-lg"
                  placeholder="+34600111222, +34600333444" value={teamPhones}
                  onChange={(e) => setTeamPhones(e.target.value)} />
              </div>
              <Button data-testid="team-phones-save-btn" className="h-14 text-lg"
                onClick={() => act(() => http.put("/admin/team-phones", {
                  phones: teamPhones.split(",").map((p) => p.trim()).filter(Boolean),
                }), "Teléfonos del equipo guardados.")}>
                Guardar teléfonos
              </Button>
              <Button data-testid="team-summary-btn" variant="outline" className="h-14 text-lg"
                onClick={() => act(() => http.post("/admin/team-summary/run"),
                  "Resumen del día enviado al equipo.")}>
                Enviar el resumen de hoy
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <Table testid="notifications-table" head={["Fecha", "Teléfono", "Tipo", "Estado", "Mensaje"]}
              rows={notifs.map((n) => (
                <tr key={n.id} className="border-b border-border">
                  <td className="py-3 pr-4">{n.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-3 pr-4">{n.to || "—"}</td>
                  <td className="py-3 pr-4">{n.kind}</td>
                  <td className="py-3 pr-4">{n.status}</td>
                  <td className="py-3 pr-4">{n.body}</td>
                </tr>
              ))} />
          </div>
        </Card>
      )}
    </Shell>
  );
}
